import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import { useNotification } from '../contexts/NotificationContext';

function sourceLabel(source) {
  if (source === 'manual') return 'Manual';
  if (source === 'inferred') return 'Inferida';
  return 'Detectada';
}

function columnsForTable(tables, tableId) {
  const t = (tables || []).find((x) => x.id === tableId);
  return t?.columns || [];
}

const ModelRelationshipManager = ({
  relationships = [],
  tables = [],
  modelTableIds = [],
  onUpdateRelationship,
  onDeleteRelationship,
  onAddRelationship,
}) => {
  const { showError } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [manual, setManual] = useState({
    fromTableId: '',
    fromColumn: '',
    toTableId: '',
    toColumn: '',
    label: '',
  });

  const modelTables = useMemo(
    () => (tables || []).filter((t) => modelTableIds.includes(t.id)),
    [tables, modelTableIds]
  );

  const visibleRels = useMemo(() => {
    const ids = new Set(modelTableIds);
    return (relationships || []).filter((r) => ids.has(r.fromTableId) && ids.has(r.toTableId));
  }, [relationships, modelTableIds]);

  const filteredRels = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleRels;
    return visibleRels.filter((r) => {
      const haystack = [
        r.fromTableId,
        r.toTableId,
        r.fromColumn,
        r.toColumn,
        r.joinHint,
        r.label,
        r.confirmed ? 'activa' : 'pendiente',
        sourceLabel(r.source),
        r.source,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [visibleRels, search]);

  const handleAdd = () => {
    const { fromTableId, fromColumn, toTableId, toColumn, label } = manual;
    if (!fromTableId || !fromColumn || !toTableId || !toColumn) {
      showError('Completa tablas y columnas de la relación');
      return;
    }
    onAddRelationship?.({
      id: `rel-manual-${Date.now().toString(36)}`,
      fromTableId,
      toTableId,
      fromColumn,
      toColumn,
      joinHint: `${fromTableId}.${fromColumn} = ${toTableId}.${toColumn}`,
      label: label || `${fromTableId} → ${toTableId}`,
      confirmed: true,
      source: 'manual',
    });
    setDialogOpen(false);
    setManual({ fromTableId: '', fromColumn: '', toTableId: '', toColumn: '', label: '' });
  };

  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <LinkIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
          Relaciones ({visibleRels.filter((r) => r.confirmed).length} activas ·{' '}
          {visibleRels.filter((r) => !r.confirmed).length} pendientes)
          {search.trim() && visibleRels.length > 0
            ? ` · ${filteredRels.length} de ${visibleRels.length}`
            : ''}
        </Typography>
        {visibleRels.length > 0 && (
          <TextField
            size="small"
            placeholder="Buscar relación…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 180, maxWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title={modelTables.length < 2 ? 'Selecciona al menos 2 tablas en el modelo primero' : ''}>
            <span>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
                disabled={modelTables.length < 2}
              >
                Relación manual
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {visibleRels.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          {modelTableIds.length < 2
            ? 'Selecciona al menos 2 tablas en el modelo.'
            : 'Usa "Detectar relaciones" o crea una relación manual.'}
        </Typography>
      ) : filteredRels.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Ninguna relación coincide con «{search.trim()}».
        </Typography>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 120, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Activa</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Relación</TableCell>
                <TableCell>Origen</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRels.map((r) => (
                <TableRow key={r.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={Boolean(r.confirmed)}
                      onChange={(e) => onUpdateRelationship?.(r.id, { confirmed: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.confirmed ? 'Activa' : 'Pendiente'}
                      color={r.confirmed ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {r.fromTableId} → {r.toTableId}
                    </Typography>
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                      {r.fromColumn} → {r.toColumn}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={sourceLabel(r.source)}
                      color={r.source === 'manual' ? 'secondary' : r.source === 'inferred' ? 'info' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const note =
                          r.source === 'manual'
                            ? ''
                            : '\n\nNo se volverá a crear al escanear ni al detectar relaciones.';
                        if (
                          window.confirm(
                            `¿Eliminar la relación ${r.fromTableId}.${r.fromColumn} → ${r.toTableId}.${r.toColumn}?${note}`
                          )
                        ) {
                          onDeleteRelationship?.(r.id);
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva relación manual</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Tabla origen (lado muchos)</InputLabel>
            <Select
              label="Tabla origen (lado muchos)"
              value={manual.fromTableId}
              onChange={(e) => setManual((p) => ({ ...p, fromTableId: e.target.value, fromColumn: '' }))}
            >
              {modelTables.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!manual.fromTableId}>
            <InputLabel>Columna origen (FK)</InputLabel>
            <Select
              label="Columna origen (FK)"
              value={manual.fromColumn}
              onChange={(e) => setManual((p) => ({ ...p, fromColumn: e.target.value }))}
            >
              {columnsForTable(tables, manual.fromTableId).map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name}
                  {c.isForeignKey ? ' (FK)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Tabla destino (lado uno)</InputLabel>
            <Select
              label="Tabla destino (lado uno)"
              value={manual.toTableId}
              onChange={(e) => setManual((p) => ({ ...p, toTableId: e.target.value, toColumn: '' }))}
            >
              {modelTables.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!manual.toTableId}>
            <InputLabel>Columna destino (PK)</InputLabel>
            <Select
              label="Columna destino (PK)"
              value={manual.toColumn}
              onChange={(e) => setManual((p) => ({ ...p, toColumn: e.target.value }))}
            >
              {columnsForTable(tables, manual.toTableId).map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name}
                  {c.isPrimaryKey ? ' (PK)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label="Etiqueta (opcional)"
            value={manual.label}
            onChange={(e) => setManual((p) => ({ ...p, label: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAdd}>
            Crear relación
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ModelRelationshipManager;
